/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { ListParams } from '@squiz/dxp-ai-client';
import { AiType } from '@squiz/dxp-ai-types';
import { Breadcrumbs, BreadcrumbsProps, ICON_CHAT_UNREAD } from '@squiz/sds';
import { DxpScreen } from 'apps/console-core/src/Components/DxpScreen/DxpScreen';
import { DXP_SCREEN_STRUCTURE_STANDARD } from 'apps/console-core/src/Components/DxpScreen/types/DxpScreenStructure.types';
import { useCapabilityBreadcrumbs } from 'apps/console-core/src/Hooks/UseCapabilityBreadcrumbs/UseCapabilityBreadcrumbs';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiDataList } from '../../Components/AiDataList/AiDataList';
import { AiScreenBanner } from '../../Components/AiScreenBanner/AiScreenBanner';
import { AI_STRINGS } from '../../Constants/Strings.constants';
import { usePaginatedChatbotList } from '../../Services/ai';

/**
 * The AI chatbots list screen.
 */
export const AiChatbots = (): JSX.Element => {
  const { t } = useTranslation();

  // Labels
  const labels = useMemo(() => {
    return {
      description: t(AI_STRINGS.aiChatbotsDescription),
      heading: t(AI_STRINGS.aiChatbots),
    };
  }, [t]);

  const [query, setQuery] = useState<ListParams>({ pageSize: 20 });
  const paginationObservable = usePaginatedChatbotList(query);

  // Breadcrumbs
  const capabilityBreadcrumbs = useCapabilityBreadcrumbs();
  const breadcrumbProps: BreadcrumbsProps = {
    currentPageName: labels.heading,
    links: capabilityBreadcrumbs,
  };
  const breadcrumbs = <Breadcrumbs {...breadcrumbProps} data-id="ai-chatbots-breadcrumbs" />;
  const screenTitle = ''; // Exclude heading from doc title
  const screenHeading = labels.heading;
  const screenContent = (
    <>
      <AiScreenBanner aiType={AiType.chatbot} description={labels.description} />
      <AiDataList paginationObservable={paginationObservable} setQuery={setQuery} type={AiType.chatbot}></AiDataList>
    </>
  );

  return (
    <DxpScreen
      breadcrumbs={breadcrumbs}
      heading={screenHeading}
      headingIcon={ICON_CHAT_UNREAD}
      structure={DXP_SCREEN_STRUCTURE_STANDARD}
      title={screenTitle}
    >
      {screenContent}
    </DxpScreen>
  );
};
